import React, { useMemo, useState } from 'react';
import { fmt$, fmtBarDate } from './utils';

const DIRECTION_COLOR = { long: '#26a641', short: '#d29922' };
const EXIT_REASON_LABEL = { signal: 'Signal', stop_loss: 'Stop-Loss', take_profit: 'Take-Profit' };
const OPERATOR_LABEL = {
  crosses_above: 'crosses above',
  crosses_below: 'crosses below',
  greater_than: '>',
  less_than: '<',
  equals: '=',
};

function fmtVal(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 });
}

// Renders the condition/logic tree captured by engine.py's _describe_signal:
// {type: 'condition', operator, result, left: {label, value}, right: {...}}
// or {type: 'logic', operator, result, inputs: [...]}.
function SignalBreakdown({ node, depth = 0 }) {
  if (!node) return null;
  if (node.type === 'logic') {
    return (
      <div style={{ paddingLeft: depth * 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: node.result ? 'var(--text)' : 'var(--muted)' }}>
          <span style={{ color: node.result ? '#26a641' : '#f85149', marginRight: 6 }}>{node.result ? '✓' : '✗'}</span>
          {String(node.operator || '').toUpperCase()}
        </div>
        {(node.inputs || []).map((input, i) => (
          <SignalBreakdown key={i} node={input} depth={depth + 1} />
        ))}
      </div>
    );
  }
  return (
    <div style={{ paddingLeft: depth * 14, fontSize: 12, color: node.result ? 'var(--text)' : 'var(--muted)', lineHeight: 1.6 }}>
      <span style={{ color: node.result ? '#26a641' : '#f85149', marginRight: 6 }}>{node.result ? '✓' : '✗'}</span>
      {node.left?.label} ({fmtVal(node.left?.value)}) {OPERATOR_LABEL[node.operator] || node.operator} {node.right?.label} ({fmtVal(node.right?.value)})
    </div>
  );
}

function TriggerDetail({ trigger, fallback }) {
  if (!trigger || trigger.length === 0) {
    return <div style={{ fontSize: 12, color: 'var(--muted)' }}>{fallback}</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {trigger.map((node, i) => <SignalBreakdown key={i} node={node} />)}
    </div>
  );
}

function TradeRow({ trade, index, showSymbol }) {
  const [open, setOpen] = useState(false);
  const isWin = (trade.pnl ?? 0) >= 0;
  const dirColor = DIRECTION_COLOR[trade.direction] || 'var(--text)';
  const colSpan = showSymbol ? 10 : 9;

  return (
    <>
      <tr
        onClick={() => setOpen((o) => !o)}
        style={{ cursor: 'pointer', borderBottom: open ? 'none' : '1px solid var(--border)' }}
      >
        <td style={{ padding: '8px 10px', color: 'var(--muted)', fontSize: 11 }}>{index + 1}</td>
        {showSymbol && <td style={{ padding: '8px 10px', fontWeight: 700, fontSize: 11 }}>{trade.symbol || '—'}</td>}
        <td style={{ padding: '8px 10px', fontWeight: 700, color: dirColor, fontSize: 11, textTransform: 'uppercase' }}>{trade.direction}</td>
        <td style={{ padding: '8px 10px', fontSize: 12, whiteSpace: 'nowrap' }}>{fmtBarDate(trade.entry_date)}</td>
        <td style={{ padding: '8px 10px', fontSize: 12, whiteSpace: 'nowrap' }}>{trade.exit_date ? fmtBarDate(trade.exit_date) : '—'}</td>
        <td style={{ padding: '8px 10px', fontSize: 12, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>{fmt$(trade.entry_price)}</td>
        <td style={{ padding: '8px 10px', fontSize: 12, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>{trade.exit_price != null ? fmt$(trade.exit_price) : '—'}</td>
        <td style={{ padding: '8px 10px', fontSize: 12, fontFamily: 'var(--font-mono)', fontWeight: 700, color: isWin ? '#26a641' : '#f85149', whiteSpace: 'nowrap' }}>
          {fmt$(trade.pnl)}{trade.pnl_pct != null ? ` (${trade.pnl_pct.toFixed(2)}%)` : ''}
        </td>
        <td style={{ padding: '8px 10px', fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap' }}>{EXIT_REASON_LABEL[trade.exit_reason] || trade.exit_reason || '—'}</td>
        <td style={{ padding: '8px 10px', fontSize: 11, color: 'var(--muted)', textAlign: 'center' }}>{open ? '▲' : '▼'}</td>
      </tr>
      {open && (
        <tr style={{ borderBottom: '1px solid var(--border)' }}>
          <td colSpan={colSpan} style={{ padding: '4px 10px 16px', background: 'var(--card)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginTop: 8 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase' }}>Why it entered</div>
                <TriggerDetail trigger={trade.entry_trigger} fallback="No condition breakdown recorded for this entry." />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase' }}>Why it exited</div>
                {!trade.exit_date && (
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>Position still open at the end of the backtest.</div>
                )}
                {trade.exit_date && trade.exit_reason === 'stop_loss' && (
                  <div style={{ fontSize: 12, color: 'var(--text)' }}>Stop-loss hit at {fmt$(trade.stop_price)}.</div>
                )}
                {trade.exit_date && trade.exit_reason === 'take_profit' && (
                  <div style={{ fontSize: 12, color: 'var(--text)' }}>Take-profit hit at {fmt$(trade.target_price)}.</div>
                )}
                {trade.exit_date && trade.exit_reason === 'signal' && (
                  <TriggerDetail trigger={trade.exit_trigger} fallback="No condition breakdown recorded for this exit." />
                )}
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase' }}>Details</div>
                <div style={{ fontSize: 12, color: 'var(--text)', display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {trade.entry_size != null && <div>Size: {trade.entry_size} shares</div>}
                  <div>
                    Duration: {trade.duration_bars ?? '—'} bar{trade.duration_bars === 1 ? '' : 's'}
                    {trade.duration_hours != null ? ` (${trade.duration_hours.toFixed(1)}h)` : ''}
                  </div>
                  {trade.stop_price != null && <div>Stop-loss: {fmt$(trade.stop_price)}</div>}
                  {trade.target_price != null && <div>Take-profit: {fmt$(trade.target_price)}</div>}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

const BASE_COLUMNS = ['#', 'Dir', 'Entry Date', 'Exit Date', 'Entry $', 'Exit $', 'PnL', 'Exit Reason', ''];

export default function TradeLogSection({ backtest, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const trades = useMemo(() => backtest?.results?.closed_trades || [], [backtest]);
  // Show the Symbol column only for multi-asset runs.
  const showSymbol = Object.keys(backtest?.price_series_map || {}).length > 1;
  const columns = showSymbol
    ? ['#', 'Symbol', 'Dir', 'Entry Date', 'Exit Date', 'Entry $', 'Exit $', 'PnL', 'Exit Reason', '']
    : BASE_COLUMNS;

  if (trades.length === 0) return null;

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          display: 'flex', alignItems: 'center', gap: 8,
          marginTop: 28, marginBottom: open ? 8 : 0,
        }}
      >
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>{open ? '▼' : '▶'}</span>
        <h3 style={{ margin: 0, color: 'var(--text)' }}>Trade Log</h3>
      </button>
      {!open && (
        <div style={{ fontSize: 11, color: 'var(--muted)' }}>
          Every trade in this backtest, with a click-to-expand breakdown of exactly why it entered and exited — click to expand.
        </div>
      )}
      {open && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {columns.map((h, ci) => (
                  <th key={ci} style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--muted)', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trades.map((t, i) => <TradeRow key={i} trade={t} index={i} showSymbol={showSymbol} />)}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
